import { useState, useMemo, useCallback } from "react";
import { initializeApp } from "firebase/app";
import { getFirestore, collection, addDoc, getDocs, doc, updateDoc, query, orderBy } from "firebase/firestore";

// ═══════════════════════════════════════════════
// CONFIG
// ═══════════════════════════════════════════════
const FIREBASE_CONFIG = {
  apiKey: "AIzaSyCYBx6nZ2W3oz07BI85QZsJtqtBN3VK8WY",
  authDomain: "snapivite-sales.firebaseapp.com",
  projectId: "snapivite-sales",
  storageBucket: "snapivite-sales.firebasestorage.app",
  messagingSenderId: "1019302285868",
  appId: "1:1019302285868:web:db3900a47d47eac0df7789",
  measurementId: "G-NZFG5HW2R4"
};
const DASHBOARD_PASSWORD = "m@K5ffT%WkLD#C4Z";

const PINK = "#ED1458";
const PURPLE = "#271F61";
const ORANGE = "#F16034";

// ═══════════════════════════════════════════════
// ROLE DEFINITIONS
// ═══════════════════════════════════════════════
const ROLES = {
  "hr-manager": {
    label: "HR Manager", family: "HR", vertical: "Group",
    salary: "₹40,000–50,000", reportsTo: "Nitesh (Founder)",
    voiceNoteQ: "Tell me about one HR process you built or fixed from scratch — what was broken, what did you do, and what was the result?",
    portfolioNeeded: "hidden",
    experienceOptions: ["1-2 years", "2-3 years", "3-4 years", "4-5 years", "5+ years"]
  },
  "designer-intern": {
    label: "Design Intern (AI)", family: "Designer", vertical: "Snapivite",
    salary: "₹6,000–8,000", reportsTo: "Ahana",
    voiceNoteQ: "Show me ONE piece of your work you're most proud of. Describe: what was the brief, what was your creative decision, and what would you do differently now?",
    portfolioNeeded: "required",
    experienceOptions: ["Student", "Fresher", "Less than 6 months", "6 months to 1 year"]
  },
  "designer-junior": {
    label: "Junior Designer", family: "Designer", vertical: "Snapivite",
    salary: "₹15,000–20,000", reportsTo: "Ahana",
    voiceNoteQ: "Show me ONE piece of your work you're most proud of. Describe: what was the brief, what was your creative decision, and what would you do differently now?",
    portfolioNeeded: "required",
    experienceOptions: ["Less than 1 year", "1-2 years", "2-3 years"]
  },
  "designer-mid": {
    label: "Mid-Level Designer", family: "Designer", vertical: "Snapivite",
    salary: "₹20,000–30,000", reportsTo: "Ahana",
    voiceNoteQ: "Show me ONE piece of your work you're most proud of. Describe: what was the brief, what was your creative decision, and what would you do differently now?",
    portfolioNeeded: "required",
    experienceOptions: ["2-3 years", "3-4 years", "4-5 years", "5+ years"]
  },
  "sales-coordinator": {
    label: "Sales Coordinator (Snapivite)", family: "Sales", vertical: "Snapivite",
    salary: "₹15,000–20,000", reportsTo: "Nitesh (Founder)",
    voiceNoteQ: "Tell me about the toughest sale or client interaction you have handled. What was the objection, and how did you overcome it?",
    portfolioNeeded: "hidden",
    experienceOptions: ["Fresher", "Less than 1 year", "1-2 years"]
  },
  "sales-executive-snapivite": {
    label: "Sales Executive (Snapivite)", family: "Sales", vertical: "Snapivite",
    salary: "₹25,000–35,000", reportsTo: "Nitesh (Founder)",
    voiceNoteQ: "Tell me about the toughest sale or client interaction you have handled. What was the objection, and how did you overcome it?",
    portfolioNeeded: "hidden",
    experienceOptions: ["2-3 years", "3-4 years", "4-5 years", "5+ years"]
  },
  "sales-executive-aps": {
    label: "Sales Executive (APS)", family: "Sales", vertical: "APS",
    salary: "₹25,000–35,000", reportsTo: "Nitesh (Founder)",
    voiceNoteQ: "Tell me about the toughest sale or client interaction you have handled. What was the objection, and how did you overcome it?",
    portfolioNeeded: "hidden",
    experienceOptions: ["2-3 years", "3-4 years", "4-5 years", "5+ years"]
  },
  "design-coordinator": {
    label: "Design Project Coordinator", family: "Design Coordinator", vertical: "Both",
    salary: "₹20,000–30,000", reportsTo: "Ahana",
    voiceNoteQ: "Tell me about a project you managed end-to-end. What broke, and how did you fix it?",
    portfolioNeeded: "optional",
    experienceOptions: ["1-2 years", "2-3 years", "3-4 years", "4-5 years"]
  },
  "social-media": {
    label: "Social Media Executive", family: "Social Media", vertical: "Snapivite",
    salary: "₹20,000–30,000", reportsTo: "Ahana",
    voiceNoteQ: "Tell me about ONE campaign or post that performed well for you. What was your thinking, and what were the numbers?",
    portfolioNeeded: "required",
    experienceOptions: ["1-2 years", "2-3 years", "3-4 years"]
  }
};

const ROLE_KEYS = Object.keys(ROLES);
const ROLE_FAMILIES = ["HR", "Designer", "Sales", "Design Coordinator", "Social Media"];

const VN_DIMENSIONS = [
  { id: "vn_specificity", label: "1. Specificity", max: 2, hint: "Named exact process/tool/work? (2=Yes, 1=Partial, 0=Vague)" },
  { id: "vn_ownership", label: "2. Ownership Language", max: 2, hint: "Uses I built/fixed/designed/sold? (2=Clear, 1=Mixed, 0=We/team)" },
  { id: "vn_arc", label: "3. Problem-Result Arc", max: 2, hint: "States what happened AND result? (2=Both, 1=One, 0=Neither)" },
  { id: "vn_depth", label: "4. Depth of Thinking", max: 2, hint: "Shows real reflection? (2=Deep, 1=Some, 0=Rehearsed)" },
  { id: "vn_clarity", label: "5. Communication Clarity", max: 2, hint: "Structured, 30-90 sec? (2=Yes, 1=Off, 0=Rambling)" },
];
const VN_MAX = 10;

const TASK_DIMENSIONS = [
  { id: "task_q1", label: "Q1: First Response / Priority", max: 5, hint: "5=Sharp, 3=Logical shallow, 1=Generic, 0=None" },
  { id: "task_q2", label: "Q2: Deliverable Quality", max: 5, hint: "5=Usable, 3=Good gaps, 1=Template, 0=Poor" },
  { id: "task_q3", label: "Q3: Judgment / Communication", max: 5, hint: "5=Balanced, 3=One-sided, 1=Off, 0=Poor" },
];
const TASK_MAX = 15;
const COMBINED_MAX = VN_MAX + TASK_MAX;

const INTERVIEW_CATEGORIES = [
  { id: "int_core", label: "Core Role Skill", max: 10, minPass: 7 },
  { id: "int_execution", label: "Execution Ability", max: 10, minPass: 6 },
  { id: "int_thinking", label: "Thinking Quality", max: 10, minPass: 5 },
  { id: "int_communication", label: "Communication", max: 10, minPass: 5 },
  { id: "int_founder_fit", label: "Company / Founder Fit", max: 10, minPass: 6 },
  { id: "int_compliance", label: "Basics / Fundamentals", max: 5, minPass: 3 },
  { id: "int_selfaware", label: "Self-Awareness", max: 5, minPass: 3 },
];
const INTERVIEW_MAX = 60;

const BASE_QUESTIONS = [
  { id: "source", label: "Where did you find this job posting?", type: "select", required: true, options: ["Naukri.com", "LinkedIn", "Apna App", "Internshala", "Shine.com", "WhatsApp / Referral", "Instagram / Social Media", "Company Website", "Other"] },
  { id: "name", label: "Full Name", type: "text", required: true },
  { id: "email", label: "Email Address", type: "text", required: true },
  { id: "phone", label: "Phone Number", type: "text", required: true },
  { id: "city", label: "Current City", type: "text", required: true },
  { id: "currentCTC", label: "Current CTC (₹ per month)", type: "number", required: true },
  { id: "expectedCTC", label: "Expected CTC (₹ per month)", type: "number", required: true },
  { id: "noticePeriod", label: "Notice Period", type: "select", required: true, options: ["Immediate", "15 days", "30 days", "45 days", "60 days", "90 days+"] },
];

function getRoleQuestions(roleKey) {
  const role = ROLES[roleKey];
  if (!role) return [];
  const q = [
    { id: "totalExp", label: "Total Relevant Experience", type: "select", required: true, options: role.experienceOptions },
  ];
  if (role.portfolioNeeded === "required") {
    q.push({ id: "portfolioLink", label: "Portfolio / Work Samples Link (Google Drive) — REQUIRED", type: "text", required: true, placeholder: "https://drive.google.com/..." });
  } else if (role.portfolioNeeded === "optional") {
    q.push({ id: "portfolioLink", label: "Portfolio / Work Samples Link (Google Drive) — Optional", type: "text", required: false, placeholder: "https://drive.google.com/..." });
  }
  q.push({ id: "resumeLink", label: "Resume Link (Google Drive) — upload PDF to Drive and share with 'Anyone with the link'", type: "text", required: true, placeholder: "https://drive.google.com/..." });

  if (role.family === "HR") {
    q.push(
      { id: "recruitmentExp", label: "End-to-End Recruitment Experience", type: "select", required: true, options: ["Never done it", "Assisted someone else", "Independently 1 to 5 hires", "Independently 5 to 20 hires", "Independently 20+ hires"] },
      { id: "sopBuilt", label: "Built an HR SOP, policy or appraisal framework from scratch?", type: "select", required: true, options: ["No", "Yes with guidance from a senior", "Yes independently"] },
    );
  } else if (role.family === "Designer") {
    q.push(
      { id: "primaryTools", label: "Primary Design Tools You Use", type: "select", required: true, options: ["Adobe (Photoshop/Illustrator/InDesign)", "Figma", "Canva only", "AI tools (Midjourney/Firefly)", "Mix of Adobe + AI", "Mix of Figma + AI"] },
      { id: "weddingIndustryExp", label: "Prior experience in wedding/luxury/print design industry?", type: "select", required: true, options: ["No — but strong general design experience", "Some exposure via freelance", "Yes 1-2 years", "Yes 2+ years"] },
    );
  } else if (role.family === "Sales") {
    q.push(
      { id: "salesBackground", label: "Which industry did most of your sales experience come from?", type: "select", required: true, options: ["Design / Creative / Advertising", "Wedding / Events / Hospitality", "Luxury retail / Fashion", "Real estate", "B2B services", "Other"] },
      { id: "englishComfort", label: "English communication comfort level", type: "select", required: true, options: ["Fully comfortable — verbal + written", "Comfortable in writing, basic in verbal", "Comfortable in verbal, weak in writing", "Basic overall"] },
      { id: "salesType", label: "What kind of sales have you primarily done?", type: "select", required: true, options: ["B2C (direct to customers)", "B2B (to businesses/planners)", "Both B2C and B2B", "Retail counter sales"] },
    );
  } else if (role.family === "Design Coordinator") {
    q.push(
      { id: "designToolComfort", label: "Design tool comfort", type: "select", required: true, options: ["Canva expert + basic Adobe", "Canva only", "Canva + AI tools like Midjourney", "Adobe basics + Canva", "None"] },
      { id: "presentationSkill", label: "Can you build a professional client-facing presentation?", type: "select", required: true, options: ["Yes done many", "Yes handful of times", "Only helped others build", "No never"] },
    );
  } else if (role.family === "Social Media") {
    q.push(
      { id: "contentType", label: "What type of content have you personally created?", type: "select", required: true, options: ["Reels + Editing + Copy + Analytics", "Reels + Editing + Copy", "Copy + Analytics only", "Only scheduled posts", "Managed a team who did"] },
      { id: "instagramHandle", label: "Instagram handle you have managed", type: "text", required: true, placeholder: "@yourhandle" },
    );
  }
  q.push(
    { id: "sopDescription", label: `Answer the voice note question in writing (2-4 lines): "${role.voiceNoteQ}"`, type: "textarea", required: true },
    { id: "whyFounder", label: "Why do you want to work at a founder-led company like Aanandam Group?", type: "textarea", required: true },
    { id: "voiceNote", label: "Voice note status (sent to hr@snapivite.com)", type: "select", required: true, options: ["Yes already sent", "Will send within 24 hours", "No"] },
  );
  return q;
}

const FORM_SCORES = {
  noticePeriod: { "Immediate": 3, "15 days": 3, "30 days": 2, "45 days": 1, "60 days": 0, "90 days+": 0 },
  voiceNote: { "Yes already sent": 3, "Will send within 24 hours": 1, "No": 0 },
};
const FORM_SCORE_MAX = 6;

function calcFormScore(data) {
  return Object.entries(FORM_SCORES).reduce((acc, [key, map]) => acc + (map[data[key]] ?? 0), 0);
}

function getScoreColor(pct) {
  if (pct >= 75) return "#16a34a";
  if (pct >= 50) return "#d97706";
  return "#dc2626";
}
function getScoreLabel(pct) {
  if (pct >= 75) return "Strong";
  if (pct >= 50) return "Moderate";
  return "Weak";
}
function getStatusColor(s) {
  return { "New": "#6b7280", "Voice Note Review": PURPLE, "Task Sent": ORANGE, "Task Reviewed": "#0891b2", "Interview Scheduled": "#7c3aed", "Offer Sent": PINK, "Hired": "#16a34a", "Rejected": "#dc2626", "On Hold": "#d97706" }[s] || "#6b7280";
}
const ALL_STATUSES = ["New", "Voice Note Review", "Task Sent", "Task Reviewed", "Interview Scheduled", "Offer Sent", "Hired", "Rejected", "On Hold"];

let db = null;
try {
  const app = initializeApp(FIREBASE_CONFIG);
  db = getFirestore(app);
} catch(e) { console.warn("Firebase not configured:", e.message); }

async function fbSave(app) {
  if (!db) return;
  try {
    if (app.docId) {
      const ref = doc(db, "hr_applications", app.docId);
      const { docId, ...data } = app;
      await updateDoc(ref, data);
    } else {
      const ref = await addDoc(collection(db, "hr_applications"), app);
      return ref.id;
    }
  } catch(e) { console.error("Firebase save error:", e); }
}

async function fbLoad() {
  if (!db) return [];
  try {
    const q = query(collection(db, "hr_applications"), orderBy("submittedAt", "desc"));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ ...d.data(), docId: d.id }));
  } catch(e) { console.error("Firebase load error:", e); return []; }
}

function PasswordGate({ onSuccess }) {
  const [pw, setPw] = useState("");
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);
  const attempt = () => {
    if (pw === DASHBOARD_PASSWORD) onSuccess();
    else { setError(true); setShake(true); setTimeout(() => setShake(false), 500); }
  };
  return (
    <div style={{ minHeight: "100vh", background: PURPLE, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "system-ui, sans-serif", padding: 24 }}>
      <div style={{ background: "#fff", borderRadius: 20, padding: "40px 36px", maxWidth: 380, width: "100%", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", animation: shake ? "shake 0.4s ease" : "none" }}>
        <style>{`@keyframes shake { 0%,100%{transform:translateX(0)} 20%,60%{transform:translateX(-8px)} 40%,80%{transform:translateX(8px)} }`}</style>
        <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#FDF0F5", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: 24 }}>🔒</div>
        <h2 style={{ color: PURPLE, margin: "0 0 6px", fontSize: 20, fontFamily: "Georgia, serif" }}>Dashboard Access</h2>
        <p style={{ color: "#888", fontSize: 13, margin: "0 0 24px" }}>Aanandam Group — HR Team</p>
        <input type="password" value={pw} onChange={e => { setPw(e.target.value); setError(false); }} onKeyDown={e => e.key === "Enter" && attempt()} placeholder="Enter password" style={{ width: "100%", padding: "12px 16px", borderRadius: 10, border: `2px solid ${error ? "#dc2626" : "#e0e0e0"}`, fontSize: 15, outline: "none", boxSizing: "border-box", textAlign: "center", letterSpacing: 3 }} autoFocus />
        {error && <p style={{ color: "#dc2626", fontSize: 12, margin: "8px 0 0" }}>Incorrect password</p>}
        <button onClick={attempt} style={{ width: "100%", marginTop: 16, padding: 14, background: PINK, color: "#fff", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer" }}>Unlock Dashboard</button>
      </div>
    </div>
  );
}

function RolePicker({ onSelect }) {
  return (
    <div style={{ minHeight: "100vh", background: "#f9f9fb", fontFamily: "system-ui, sans-serif" }}>
      <div style={{ background: PURPLE, padding: "32px 24px", textAlign: "center" }}>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", letterSpacing: 3, marginBottom: 8 }}>AANANDAM GROUP</div>
        <h1 style={{ color: "#fff", fontSize: 24, fontFamily: "Georgia, serif", margin: "0 0 6px" }}>Career Opportunities</h1>
        <p style={{ color: "rgba(255,255,255,0.65)", fontSize: 13, margin: 0 }}>Jaipur · Snapivite & Aanandam Pack Studio</p>
      </div>
      <div style={{ maxWidth: 660, margin: "0 auto", padding: "28px 16px 60px" }}>
        <p style={{ color: "#555", fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>Select the role you are applying for. Each role has its own application form.</p>
        {ROLE_FAMILIES.map(fam => {
          const rolesInFamily = ROLE_KEYS.filter(k => ROLES[k].family === fam);
          if (rolesInFamily.length === 0) return null;
          return (
            <div key={fam} style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: PINK, fontWeight: 700, letterSpacing: 2, marginBottom: 10 }}>{fam.toUpperCase()}</div>
              {rolesInFamily.map(k => {
                const r = ROLES[k];
                return (
                  <button key={k} onClick={() => onSelect(k)}
                    style={{ display: "block", width: "100%", background: "#fff", border: "1.5px solid #e8e8ed", borderRadius: 12, padding: "16px 20px", marginBottom: 10, cursor: "pointer", textAlign: "left", transition: "all 0.2s" }}
                    onMouseOver={e => { e.currentTarget.style.borderColor = PINK; }}
                    onMouseOut={e => { e.currentTarget.style.borderColor = "#e8e8ed"; }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: PURPLE, marginBottom: 4 }}>{r.label}</div>
                        <div style={{ fontSize: 12, color: "#888" }}>{r.vertical}  ·  Reports to {r.reportsTo}</div>
                      </div>
                      <div style={{ color: PINK, fontSize: 20, fontWeight: 700, flexShrink: 0 }}>›</div>
                    </div>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ApplicationForm({ roleKey, onSubmit, onBack }) {
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const role = ROLES[roleKey];
  const questions = [...BASE_QUESTIONS, ...getRoleQuestions(roleKey)];
  const set = (id, val) => setForm(f => ({ ...f, [id]: val }));

  const handleSubmit = async () => {
    const e = {};
    questions.forEach(q => { if (q.required && !form[q.id]) e[q.id] = true; });
    setErrors(e);
    if (Object.keys(e).length > 0) { window.scrollTo(0, 0); return; }
    setSubmitting(true);
    const app = {
      ...form, roleKey, roleLabel: role.label, roleFamily: role.family, roleVertical: role.vertical,
      id: Date.now().toString(), submittedAt: Date.now(),
      formScore: calcFormScore(form), status: "New",
      vnScores: {}, taskScores: {}, intScores: {}, notes: ""
    };
    const docId = await fbSave(app);
    if (docId) app.docId = docId;
    onSubmit(app);
    setSubmitted(true);
    setSubmitting(false);
  };

  if (submitted) return (
    <div style={{ minHeight: "100vh", background: "#f9f9fb", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: "40px 32px", maxWidth: 460, textAlign: "center", boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}>
        <div style={{ width: 64, height: 64, borderRadius: "50%", background: PINK, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: 28, color: "#fff" }}>✓</div>
        <h2 style={{ color: PURPLE, fontFamily: "Georgia, serif", fontSize: 22, margin: "0 0 12px" }}>Application Received</h2>
        <p style={{ color: "#555", lineHeight: 1.7, marginBottom: 20, fontSize: 14 }}>Thank you for applying to <strong>{role.label}</strong>. If your profile matches we will reach out within 5 working days.</p>
        <div style={{ background: "#FDF0F5", borderRadius: 10, padding: "14px 18px" }}>
          <p style={{ color: PINK, fontWeight: 700, fontSize: 13, margin: 0 }}>Send your 90-second voice note to hr@snapivite.com now.</p>
        </div>
      </div>
    </div>
  );

  const errorCount = Object.keys(errors).length;
  return (
    <div style={{ minHeight: "100vh", background: "#f9f9fb", fontFamily: "system-ui, sans-serif" }}>
      <div style={{ background: PURPLE, padding: "24px 20px" }}>
        <div style={{ maxWidth: 660, margin: "0 auto" }}>
          <button onClick={onBack} style={{ background: "rgba(255,255,255,0.12)", border: "none", color: "#fff", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 12, marginBottom: 12 }}>← Change Role</button>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", letterSpacing: 3, marginBottom: 6 }}>AANANDAM GROUP · {role.vertical.toUpperCase()}</div>
          <h1 style={{ color: "#fff", fontSize: 22, fontFamily: "Georgia, serif", margin: "0 0 6px" }}>{role.label}</h1>
          <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 13, margin: 0 }}>Jaipur · Full-time · {role.salary}/month</p>
        </div>
      </div>
      <div style={{ maxWidth: 660, margin: "0 auto", padding: "24px 16px 80px" }}>
        {errorCount > 0 && (
          <div style={{ background: "#FEF2F2", border: "1px solid #dc2626", borderRadius: 10, padding: "12px 16px", marginBottom: 20 }}>
            <p style={{ margin: 0, fontSize: 13, color: "#dc2626", fontWeight: 600 }}>Please fill in {errorCount} required field{errorCount > 1 ? "s" : ""} below</p>
          </div>
        )}
        <div style={{ background: "#FFF7ED", border: `1px solid ${ORANGE}`, borderRadius: 10, padding: "14px 16px", marginBottom: 20 }}>
          <p style={{ margin: "0 0 8px", fontSize: 13, color: "#7C4A00", fontWeight: 700 }}>Two things must happen for your application to be reviewed:</p>
          <p style={{ margin: "0 0 6px", fontSize: 12, color: "#7C4A00", lineHeight: 1.6 }}>1. Complete this form</p>
          <p style={{ margin: 0, fontSize: 12, color: "#7C4A00", lineHeight: 1.6 }}>2. Email a 90-second voice note to <strong>hr@snapivite.com</strong> answering: <em>"{role.voiceNoteQ}"</em></p>
        </div>
        {questions.map((q, i) => (
          <div key={q.id} style={{ background: "#fff", borderRadius: 12, padding: "18px 20px", marginBottom: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.05)", border: errors[q.id] ? "1.5px solid #dc2626" : "1.5px solid transparent" }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: PURPLE, marginBottom: 10, lineHeight: 1.5 }}>
              {i + 1}. {q.label} {q.required && <span style={{ color: PINK }}>*</span>}
            </label>
            {(q.type === "text" || q.type === "number") && (
              <input type={q.type} value={form[q.id] || ""} onChange={e => set(q.id, e.target.value)} placeholder={q.placeholder || ""}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e0e0e0", fontSize: 13, outline: "none", boxSizing: "border-box" }} />
            )}
            {q.type === "select" && (
              <select value={form[q.id] || ""} onChange={e => set(q.id, e.target.value)}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e0e0e0", fontSize: 13, outline: "none", background: "#fff" }}>
                <option value="">Select...</option>
                {q.options.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            )}
            {q.type === "textarea" && (
              <textarea value={form[q.id] || ""} onChange={e => set(q.id, e.target.value)} rows={4}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e0e0e0", fontSize: 13, outline: "none", resize: "vertical", boxSizing: "border-box" }} />
            )}
            {errors[q.id] && <p style={{ color: "#dc2626", fontSize: 11, margin: "4px 0 0" }}>This field is required</p>}
          </div>
        ))}
        <button onClick={handleSubmit} disabled={submitting}
          style={{ width: "100%", padding: 16, background: submitting ? "#ccc" : PINK, color: "#fff", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: submitting ? "not-allowed" : "pointer", marginTop: 8 }}>
          {submitting ? "Submitting..." : "Submit Application"}
        </button>
      </div>
    </div>
  );
}

function ScoreInput({ label, max, value, onChange, hint }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 13, color: "#333", fontWeight: 500 }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: value !== undefined ? getScoreColor((value / max) * 100) : "#bbb" }}>{value !== undefined ? `${value}/${max}` : "—"}</span>
      </div>
      {hint && <p style={{ fontSize: 11, color: "#888", margin: "0 0 8px", lineHeight: 1.5 }}>{hint}</p>}
      <div style={{ display: "flex", gap: 5 }}>
        {Array.from({ length: max + 1 }, (_, n) => (
          <button key={n} onClick={() => onChange(n)}
            style={{ flex: 1, padding: "9px 4px", borderRadius: 6, border: "2px solid", borderColor: value === n ? getScoreColor((n / max) * 100) : "#e0e0e0", background: value === n ? getScoreColor((n / max) * 100) : "#fff", color: value === n ? "#fff" : "#444", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div style={{ background: "#fff", borderRadius: 12, padding: "18px 18px", marginBottom: 14, boxShadow: "0 1px 6px rgba(0,0,0,0.05)" }}>
      <h3 style={{ color: PURPLE, margin: "0 0 14px", fontSize: 14, fontWeight: 700 }}>{title}</h3>
      {children}
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div style={{ display: "flex", gap: 12, padding: "7px 0", borderBottom: "1px solid #f5f5f5" }}>
      <div style={{ width: 140, fontSize: 12, color: "#aaa", flexShrink: 0 }}>{label}</div>
      <div style={{ fontSize: 13, color: "#222" }}>{value}</div>
    </div>
  );
}
function WriteAnswer({ label, value }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12, color: "#aaa", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 13, color: "#333", lineHeight: 1.7, background: "#f9f9fb", borderRadius: 8, padding: 12 }}>{value || "—"}</div>
    </div>
  );
}
function DecisionBox({ label, text, color, score }) {
  return (
    <div style={{ marginTop: 16, padding: "14px 16px", borderRadius: 10, background: color + "12", border: `2px solid ${color}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color, marginBottom: 4 }}>{label}</div>
          <div style={{ fontSize: 14, fontWeight: 800, color }}>{text}</div>
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color }}>{score}</div>
      </div>
    </div>
  );
}

function CandidateDetail({ app, onBack, onUpdate }) {
  const [local, setLocal] = useState(app);
  const [tab, setTab] = useState("profile");
  const [saving, setSaving] = useState(false);

  const update = useCallback(async (changes) => {
    const updated = { ...local, ...changes };
    setLocal(updated);
    setSaving(true);
    await fbSave(updated);
    onUpdate(updated);
    setSaving(false);
  }, [local, onUpdate]);

  const vnTotal = VN_DIMENSIONS.reduce((a, d) => a + (local.vnScores?.[d.id] ?? 0), 0);
  const vnFilled = VN_DIMENSIONS.every(d => local.vnScores?.[d.id] !== undefined);
  const taskTotal = TASK_DIMENSIONS.reduce((a, d) => a + (local.taskScores?.[d.id] ?? 0), 0);
  const taskFilled = TASK_DIMENSIONS.every(d => local.taskScores?.[d.id] !== undefined);
  const intTotal = INTERVIEW_CATEGORIES.reduce((a, d) => a + (local.intScores?.[d.id] ?? 0), 0);
  const intFilled = INTERVIEW_CATEGORIES.every(d => local.intScores?.[d.id] !== undefined);
  const combinedTotal = vnTotal + taskTotal;
  const combinedPct = Math.round((combinedTotal / COMBINED_MAX) * 100);
  const role = ROLES[local.roleKey] || {};

  const screeningDecision = () => {
    if (!vnFilled || !taskFilled) return { label: "Score both gates to see decision", color: "#aaa", bg: "#f5f5f5" };
    if (vnTotal <= 4) return { label: "REJECT — Voice note score below threshold", color: "#dc2626", bg: "#FEF2F2" };
    if (taskTotal <= 4) return { label: "REJECT — Task score below threshold", color: "#dc2626", bg: "#FEF2F2" };
    if (vnTotal >= 8 && taskTotal >= 12) return { label: "SCHEDULE INTERVIEW — Strong on both", color: "#16a34a", bg: "#F0FDF4" };
    if (vnTotal >= 8 && taskTotal >= 8) return { label: "SCHEDULE INTERVIEW — Probe task gaps", color: "#16a34a", bg: "#F0FDF4" };
    if (vnTotal >= 5 && taskTotal >= 10) return { label: "INTERVIEW WITH CAUTION", color: "#d97706", bg: "#FFF7ED" };
    return { label: "DO NOT INTERVIEW", color: "#dc2626", bg: "#FEF2F2" };
  };
  const interviewDecision = () => {
    if (!intFilled) return { label: "Score all categories", color: "#aaa" };
    const failed = INTERVIEW_CATEGORIES.filter(c => (local.intScores[c.id] ?? 0) < c.minPass);
    if (intTotal < 38) return { label: `REJECT — Total ${intTotal}/60, minimum is 38`, color: "#dc2626" };
    if (failed.length > 0) return { label: `REVIEW — Below minimum in: ${failed.map(c => c.label.split(" ")[0]).join(", ")}`, color: "#d97706" };
    return { label: `HIRE — Score ${intTotal}/60`, color: "#16a34a" };
  };
  const sd = screeningDecision();
  const id = interviewDecision();
  const tabs = ["profile", "voice note", "task", "interview", "notes"];

  return (
    <div style={{ minHeight: "100vh", background: "#f9f9fb", fontFamily: "system-ui, sans-serif" }}>
      <div style={{ background: PURPLE, padding: "14px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <button onClick={onBack} style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 13 }}>Back</button>
          <div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>{local.name}</div>
            <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 11 }}>{local.roleLabel || "HR Manager"} · {local.roleVertical || "Group"}</div>
          </div>
          {saving && <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 11 }}>Saving...</span>}
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {ALL_STATUSES.map(s => (
            <button key={s} onClick={() => update({ status: s })}
              style={{ padding: "4px 10px", borderRadius: 20, border: "1.5px solid", borderColor: local.status === s ? "#fff" : "rgba(255,255,255,0.2)", background: local.status === s ? "#fff" : "transparent", color: local.status === s ? PURPLE : "rgba(255,255,255,0.7)", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
              {s}
            </button>
          ))}
        </div>
      </div>
      <div style={{ background: "#fff", borderBottom: "1px solid #eee", padding: "12px 16px", display: "flex", gap: 16, overflowX: "auto" }}>
        {[
          { label: "Form", val: local.formScore, max: FORM_SCORE_MAX },
          { label: "Voice Note", val: vnFilled ? vnTotal : null, max: VN_MAX },
          { label: "Task", val: taskFilled ? taskTotal : null, max: TASK_MAX },
          { label: "Screening", val: vnFilled && taskFilled ? combinedTotal : null, max: COMBINED_MAX },
          { label: "Interview", val: intFilled ? intTotal : null, max: INTERVIEW_MAX },
        ].map(({ label, val, max }) => {
          const pct = val !== null ? Math.round((val / max) * 100) : null;
          return (
            <div key={label} style={{ textAlign: "center", minWidth: 70, flexShrink: 0 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: pct !== null ? getScoreColor(pct) : "#ccc" }}>{val !== null ? `${val}/${max}` : "—"}</div>
              <div style={{ fontSize: 11, color: "#888" }}>{label}</div>
              {pct !== null && <div style={{ fontSize: 10, fontWeight: 600, color: getScoreColor(pct) }}>{getScoreLabel(pct)}</div>}
            </div>
          );
        })}
      </div>
      <div style={{ background: "#fff", borderBottom: "1px solid #eee", display: "flex", overflowX: "auto" }}>
        {tabs.map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{ padding: "11px 14px", border: "none", background: "none", color: tab === t ? PINK : "#888", fontWeight: tab === t ? 700 : 400, fontSize: 12, cursor: "pointer", borderBottom: tab === t ? `3px solid ${PINK}` : "3px solid transparent", whiteSpace: "nowrap", textTransform: "capitalize" }}>
            {t}
          </button>
        ))}
      </div>
      <div style={{ maxWidth: 700, margin: "0 auto", padding: "16px 14px 60px" }}>
        {tab === "profile" && (
          <div>
            <Card title="Application Details">
              <Row label="Role Applied" value={<><span style={{ color: PURPLE, fontWeight: 600 }}>{local.roleLabel || "HR Manager"}</span><span style={{ fontSize: 11, color: "#888", marginLeft: 6 }}>({local.roleVertical || "Group"})</span></>} />
              <Row label="Source" value={local.source || "Unknown"} />
              <Row label="Submitted At" value={new Date(local.submittedAt).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true })} />
            </Card>
            <Card title="Contact Details">
              {[["Email", local.email], ["Phone", local.phone], ["City", local.city], ["Current CTC", `₹${Number(local.currentCTC).toLocaleString("en-IN")}/month`], ["Expected CTC", `₹${Number(local.expectedCTC).toLocaleString("en-IN")}/month`], ["Notice Period", local.noticePeriod]].map(([k, v]) => (
                <Row key={k} label={k} value={v} />
              ))}
            </Card>
            {(local.resumeLink || local.portfolioLink || local.instagramHandle) && (
              <Card title="Documents & Links">
                {local.resumeLink && (
                  <div style={{ padding: "10px 0", borderBottom: "1px solid #f5f5f5" }}>
                    <div style={{ fontSize: 12, color: "#aaa", marginBottom: 6 }}>Resume</div>
                    <a href={local.resumeLink} target="_blank" rel="noopener noreferrer"
                      style={{ display: "inline-block", background: PINK, color: "#fff", padding: "8px 14px", borderRadius: 8, textDecoration: "none", fontSize: 13, fontWeight: 600 }}>
                      Open Resume ↗
                    </a>
                  </div>
                )}
                {local.portfolioLink && (
                  <div style={{ padding: "10px 0", borderBottom: local.instagramHandle ? "1px solid #f5f5f5" : "none" }}>
                    <div style={{ fontSize: 12, color: "#aaa", marginBottom: 6 }}>Portfolio</div>
                    <a href={local.portfolioLink} target="_blank" rel="noopener noreferrer"
                      style={{ display: "inline-block", background: PURPLE, color: "#fff", padding: "8px 14px", borderRadius: 8, textDecoration: "none", fontSize: 13, fontWeight: 600 }}>
                      Open Portfolio ↗
                    </a>
                  </div>
                )}
                {local.instagramHandle && (
                  <div style={{ padding: "10px 0" }}>
                    <div style={{ fontSize: 12, color: "#aaa", marginBottom: 6 }}>Instagram</div>
                    <div style={{ fontSize: 14, color: PURPLE, fontWeight: 600 }}>{local.instagramHandle}</div>
                  </div>
                )}
              </Card>
            )}
            <Card title="Experience & Background">
              {[
                ["Total Experience", local.totalExp],
                local.recruitmentExp && ["Recruitment Exp", local.recruitmentExp],
                local.sopBuilt && ["SOP Built", local.sopBuilt],
                local.primaryTools && ["Primary Tools", local.primaryTools],
                local.weddingIndustryExp && ["Wedding/Luxury Exp", local.weddingIndustryExp],
                local.salesBackground && ["Sales Background", local.salesBackground],
                local.englishComfort && ["English Comfort", local.englishComfort],
                local.salesType && ["Sales Type", local.salesType],
                local.designToolComfort && ["Design Tool Comfort", local.designToolComfort],
                local.presentationSkill && ["Presentation Skill", local.presentationSkill],
                local.contentType && ["Content Type", local.contentType],
                ["Voice Note Status", local.voiceNote],
              ].filter(Boolean).map(([k, v]) => <Row key={k} label={k} value={v} />)}
            </Card>
            <Card title="Written Answers">
              <WriteAnswer label="Answer to Voice Note Question (written)" value={local.sopDescription} />
              <WriteAnswer label="Why Aanandam Group" value={local.whyFounder} />
            </Card>
          </div>
        )}
        {tab === "voice note" && (
          <Card title={`Voice Note Scoring — ${vnFilled ? `${vnTotal}/${VN_MAX}` : "Pending"}`}>
            <p style={{ fontSize: 13, color: "#888", marginBottom: 8 }}>Question: <em>"{role.voiceNoteQ || "N/A"}"</em></p>
            <p style={{ fontSize: 12, color: "#aaa", marginBottom: 16 }}>Score each dimension based on rubric hints.</p>
            {VN_DIMENSIONS.map(d => (
              <ScoreInput key={d.id} label={d.label} max={d.max} value={local.vnScores?.[d.id]} onChange={v => update({ vnScores: { ...local.vnScores, [d.id]: v } })} hint={d.hint} />
            ))}
            {vnFilled && <DecisionBox label="Voice Note Decision" text={vnTotal >= 8 ? "Strong. Send task." : vnTotal >= 5 ? "Borderline. Send task, flag." : "Reject. No task."} color={vnTotal >= 8 ? "#16a34a" : vnTotal >= 5 ? "#d97706" : "#dc2626"} score={`${vnTotal}/${VN_MAX}`} />}
          </Card>
        )}
        {tab === "task" && (
          <div>
            <Card title={`Async Task Scoring — ${taskFilled ? `${taskTotal}/${TASK_MAX}` : "Pending"}`}>
              <p style={{ fontSize: 13, color: "#888", marginBottom: 16 }}>Three questions. 48-hour deadline. Max 5 each.</p>
              {TASK_DIMENSIONS.map(d => (
                <ScoreInput key={d.id} label={d.label} max={d.max} value={local.taskScores?.[d.id]} onChange={v => update({ taskScores: { ...local.taskScores, [d.id]: v } })} hint={d.hint} />
              ))}
            </Card>
            {vnFilled && taskFilled && (
              <div style={{ padding: "16px 18px", borderRadius: 12, background: sd.bg, border: `2px solid ${sd.color}`, marginTop: 4 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: sd.color, marginBottom: 6 }}>SCREENING DECISION</div>
                <div style={{ fontSize: 14, fontWeight: 800, color: sd.color, marginBottom: 10 }}>{sd.label}</div>
                <div style={{ display: "flex", gap: 16, fontSize: 13, color: "#555" }}>
                  <span>VN: <strong>{vnTotal}/{VN_MAX}</strong></span>
                  <span>Task: <strong>{taskTotal}/{TASK_MAX}</strong></span>
                  <span>Combined: <strong>{combinedTotal}/{COMBINED_MAX} ({combinedPct}%)</strong></span>
                </div>
              </div>
            )}
          </div>
        )}
        {tab === "interview" && (
          <Card title={`Interview Scoring — ${intFilled ? `${intTotal}/${INTERVIEW_MAX}` : "Pending"}`}>
            <p style={{ fontSize: 13, color: "#888", marginBottom: 16 }}>Minimum 38/60 to hire.</p>
            {INTERVIEW_CATEGORIES.map(c => (
              <div key={c.id} style={{ marginBottom: 18 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{c.label}</span>
                  <span style={{ fontSize: 11, color: "#aaa" }}>Min: <strong style={{ color: ORANGE }}>{c.minPass}</strong></span>
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  {Array.from({ length: c.max + 1 }, (_, n) => {
                    const sel = local.intScores?.[c.id] === n;
                    const pass = n >= c.minPass;
                    const low = n > 0 && !pass;
                    return (
                      <button key={n} onClick={() => update({ intScores: { ...local.intScores, [c.id]: n } })}
                        style={{ flex: 1, padding: "8px 2px", borderRadius: 6, border: "2px solid", borderColor: sel ? (pass ? "#16a34a" : "#dc2626") : low ? "#fca5a5" : "#e0e0e0", background: sel ? (pass ? "#16a34a" : "#dc2626") : low ? "#FEF2F2" : "#fff", color: sel ? "#fff" : "#444", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                        {n}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            {intFilled && <DecisionBox label="Interview Decision" text={id.label} color={id.color} score={`${intTotal}/${INTERVIEW_MAX}`} />}
          </Card>
        )}
        {tab === "notes" && (
          <Card title="Internal Notes">
            <p style={{ fontSize: 13, color: "#888", marginBottom: 12 }}>Auto-saves on every keystroke.</p>
            <textarea value={local.notes || ""} onChange={e => update({ notes: e.target.value })} rows={12}
              placeholder="Voice note observations, task notes, interview impressions..."
              style={{ width: "100%", padding: 14, borderRadius: 10, border: "1.5px solid #e0e0e0", fontSize: 13, lineHeight: 1.7, resize: "vertical", boxSizing: "border-box", outline: "none" }} />
          </Card>
        )}
      </div>
    </div>
  );
}

function Dashboard({ applications, onBack, onUpdateApp }) {
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterScore, setFilterScore] = useState("All");
  const [filterNotice, setFilterNotice] = useState("All");
  const [filterSource, setFilterSource] = useState("All");
  const [selected, setSelected] = useState(null);

  const filtered = useMemo(() => applications.filter(app => {
    const pct = Math.round((app.formScore / FORM_SCORE_MAX) * 100);
    if (search && !app.name?.toLowerCase().includes(search.toLowerCase()) && !app.email?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterRole !== "All" && (app.roleKey || "hr-manager") !== filterRole) return false;
    if (filterStatus !== "All" && app.status !== filterStatus) return false;
    if (filterScore !== "All" && getScoreLabel(pct) !== filterScore) return false;
    if (filterNotice !== "All" && app.noticePeriod !== filterNotice) return false;
    if (filterSource !== "All" && app.source !== filterSource) return false;
    return true;
  }), [applications, search, filterRole, filterStatus, filterScore, filterNotice, filterSource]);

  const handleUpdate = (updated) => {
    onUpdateApp(updated);
    if (selected?.id === updated.id) setSelected(updated);
  };
  if (selected) return <CandidateDetail app={selected} onBack={() => setSelected(null)} onUpdate={handleUpdate} />;

  const stats = {
    total: applications.length,
    strong: applications.filter(a => Math.round((a.formScore / FORM_SCORE_MAX) * 100) >= 75).length,
    pipeline: applications.filter(a => ["Interview Scheduled", "Offer Sent"].includes(a.status)).length,
    hired: applications.filter(a => a.status === "Hired").length,
  };

  const roleBreakdown = ROLE_KEYS.map(k => ({
    key: k, label: ROLES[k].label,
    count: applications.filter(a => (a.roleKey || "hr-manager") === k).length
  })).filter(r => r.count > 0).sort((a, b) => b.count - a.count);

  const sourceCount = applications.reduce((acc, a) => {
    const s = a.source || "Unknown";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const sourceSorted = Object.entries(sourceCount).sort((a, b) => b[1] - a[1]);

  const exportCSV = () => {
    const headers = ["Name","Role","Vertical","Source","Email","Phone","City","Current CTC","Expected CTC","Notice","Experience","Resume Link","Portfolio Link","Form Score","VN Score","Task Score","Interview Score","Status","Applied At"];
    const rows = applications.map(a => {
      const vn = VN_DIMENSIONS.reduce((s,d) => s+(a.vnScores?.[d.id]??0),0);
      const tk = TASK_DIMENSIONS.reduce((s,d) => s+(a.taskScores?.[d.id]??0),0);
      const it = INTERVIEW_CATEGORIES.reduce((s,d) => s+(a.intScores?.[d.id]??0),0);
      return [a.name,a.roleLabel||"HR Manager",a.roleVertical||"Group",a.source||"Unknown",a.email,a.phone,a.city,a.currentCTC,a.expectedCTC,a.noticePeriod,a.totalExp,a.resumeLink||"",a.portfolioLink||"",`${a.formScore}/${FORM_SCORE_MAX}`,`${vn}/${VN_MAX}`,`${tk}/${TASK_MAX}`,`${it}/${INTERVIEW_MAX}`,a.status,new Date(a.submittedAt).toLocaleString("en-IN")];
    });
    const csv=[headers,...rows].map(r=>r.map(c=>`"${(c??"").toString().replace(/"/g,'""')}"`).join(",")).join("\n");
    const bl=new Blob([csv],{type:"text/csv"});
    const el=document.createElement("a");el.href=URL.createObjectURL(bl);el.download="AG_Applications.csv";el.click();
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f9f9fb", fontFamily: "system-ui, sans-serif" }}>
      <div style={{ background: PURPLE, padding: "14px 16px" }}>
        <div style={{ maxWidth: 1080, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", letterSpacing: 2 }}>AANANDAM GROUP</div>
            <h1 style={{ color: "#fff", margin: "2px 0 0", fontSize: 17, fontFamily: "Georgia, serif" }}>Hiring Dashboard</h1>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={onBack} style={{ background: "rgba(255,255,255,0.12)", border: "none", color: "#fff", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 12 }}>View Form</button>
            <button onClick={exportCSV} style={{ background: PINK, border: "none", color: "#fff", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>Export CSV</button>
          </div>
        </div>
      </div>
      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "16px 14px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 12, marginBottom: 16 }}>
          {[["Total Applied", stats.total, PURPLE], ["Strong Profiles", stats.strong, "#16a34a"], ["In Pipeline", stats.pipeline, ORANGE], ["Hired", stats.hired, PINK]].map(([label, val, color]) => (
            <div key={label} style={{ background: "#fff", borderRadius: 12, padding: "14px 16px", boxShadow: "0 1px 6px rgba(0,0,0,0.05)", borderTop: `3px solid ${color}` }}>
              <div style={{ fontSize: 28, fontWeight: 800, color }}>{val}</div>
              <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{label}</div>
            </div>
          ))}
        </div>

        {roleBreakdown.length > 0 && (
          <div style={{ background: "#fff", borderRadius: 12, padding: "12px 16px", marginBottom: 12, boxShadow: "0 1px 6px rgba(0,0,0,0.05)" }}>
            <div style={{ fontSize: 11, color: "#888", fontWeight: 700, letterSpacing: 0.5, marginBottom: 8 }}>APPLICATIONS BY ROLE (tap to filter)</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {roleBreakdown.map(({ key, label, count }) => (
                <div key={key} onClick={() => setFilterRole(key === filterRole ? "All" : key)}
                  style={{ background: filterRole === key ? PINK : "#FDF0F5", color: filterRole === key ? "#fff" : PURPLE, borderRadius: 8, padding: "6px 12px", display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <span style={{ fontSize: 12, fontWeight: 600 }}>{label}</span>
                  <span style={{ fontSize: 13, fontWeight: 800 }}>{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {sourceSorted.length > 0 && (
          <div style={{ background: "#fff", borderRadius: 12, padding: "12px 16px", marginBottom: 14, boxShadow: "0 1px 6px rgba(0,0,0,0.05)" }}>
            <div style={{ fontSize: 11, color: "#888", fontWeight: 700, letterSpacing: 0.5, marginBottom: 8 }}>APPLICATIONS BY SOURCE</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {sourceSorted.map(([source, count]) => (
                <div key={source} style={{ background: "#F0F0F8", borderRadius: 8, padding: "6px 12px", display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 12, color: PURPLE, fontWeight: 600 }}>{source}</span>
                  <span style={{ fontSize: 13, fontWeight: 800, color: PINK }}>{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ background: "#fff", borderRadius: 12, padding: "12px 14px", marginBottom: 14, boxShadow: "0 1px 6px rgba(0,0,0,0.05)", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input placeholder="Search name or email..." value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 160, padding: "8px 12px", borderRadius: 8, border: "1.5px solid #e0e0e0", fontSize: 12, outline: "none" }} />
          <select value={filterRole} onChange={e => setFilterRole(e.target.value)} style={{ padding: "8px 10px", borderRadius: 8, border: "1.5px solid #e0e0e0", fontSize: 12, outline: "none", background: "#fff" }}>
            <option value="All">All Roles</option>
            {ROLE_KEYS.map(k => <option key={k} value={k}>{ROLES[k].label}</option>)}
          </select>
          {[["Status", filterStatus, setFilterStatus, ["All", ...ALL_STATUSES]],
            ["Score", filterScore, setFilterScore, ["All","Strong","Moderate","Weak"]],
            ["Notice", filterNotice, setFilterNotice, ["All","Immediate","15 days","30 days","45 days","60 days","90 days+"]],
            ["Source", filterSource, setFilterSource, ["All","Naukri.com","LinkedIn","Apna App","Internshala","Shine.com","WhatsApp / Referral","Instagram / Social Media","Company Website","Other"]]
          ].map(([label, val, setter, opts]) => (
            <select key={label} value={val} onChange={e => setter(e.target.value)}
              style={{ padding: "8px 10px", borderRadius: 8, border: "1.5px solid #e0e0e0", fontSize: 12, outline: "none", background: "#fff" }}>
              {opts.map(o => <option key={o}>{o}</option>)}
            </select>
          ))}
          <span style={{ fontSize: 11, color: "#888" }}>{filtered.length} result{filtered.length !== 1 ? "s" : ""}</span>
        </div>

        {filtered.length === 0 ? (
          <div style={{ background: "#fff", borderRadius: 12, padding: 48, textAlign: "center", boxShadow: "0 1px 6px rgba(0,0,0,0.05)" }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>📋</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: PURPLE, marginBottom: 6 }}>No applications match filters</div>
            <div style={{ fontSize: 13, color: "#888" }}>Try clearing filters.</div>
          </div>
        ) : (
          <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 6px rgba(0,0,0,0.05)", overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
                <thead>
                  <tr style={{ background: "#f5f5f8" }}>
                    {["Candidate","Role","Applied","Source","CTC Ask","Notice","Form","Screening","Status",""].map(h => (
                      <th key={h} style={{ padding: "9px 12px", fontSize: 10, fontWeight: 700, color: "#777", textAlign: "left", letterSpacing: 0.5 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((app, i) => {
                    const formPct = Math.round((app.formScore / FORM_SCORE_MAX) * 100);
                    const vnFilled = VN_DIMENSIONS.every(d => app.vnScores?.[d.id] !== undefined);
                    const taskFilled = TASK_DIMENSIONS.every(d => app.taskScores?.[d.id] !== undefined);
                    const vnT = VN_DIMENSIONS.reduce((s,d) => s+(app.vnScores?.[d.id]??0),0);
                    const tkT = TASK_DIMENSIONS.reduce((s,d) => s+(app.taskScores?.[d.id]??0),0);
                    const screenPct = vnFilled && taskFilled ? Math.round(((vnT+tkT)/COMBINED_MAX)*100) : null;
                    const sc = getStatusColor(app.status);
                    return (
                      <tr key={app.id} style={{ borderTop: "1px solid #f0f0f0", background: i%2===0?"#fff":"#fafafa" }}>
                        <td style={{ padding: "11px 12px" }}>
                          <div style={{ fontWeight: 600, color: PURPLE, fontSize: 13 }}>{app.name}</div>
                          <div style={{ fontSize: 11, color: "#aaa" }}>{app.city}</div>
                        </td>
                        <td style={{ padding: "11px 12px" }}>
                          <div style={{ fontSize: 12, color: PURPLE, fontWeight: 600 }}>{app.roleLabel || "HR Manager"}</div>
                          <div style={{ fontSize: 10, color: "#aaa" }}>{app.roleVertical || "Group"}</div>
                        </td>
                        <td style={{ padding: "11px 12px", fontSize: 11, color: "#555" }}>{new Date(app.submittedAt).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true })}</td>
                        <td style={{ padding: "11px 12px" }}>
                          <span style={{ fontSize: 10, fontWeight: 600, color: PURPLE, background: "#F0F0F8", padding: "3px 8px", borderRadius: 6, whiteSpace: "nowrap" }}>{app.source || "Unknown"}</span>
                        </td>
                        <td style={{ padding: "11px 12px", fontSize: 12 }}>₹{Number(app.expectedCTC).toLocaleString("en-IN")}</td>
                        <td style={{ padding: "11px 12px", fontSize: 12 }}>{app.noticePeriod}</td>
                        <td style={{ padding: "11px 12px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                            <div style={{ width: 32, height: 4, borderRadius: 2, background: "#eee", overflow: "hidden" }}>
                              <div style={{ width: `${formPct}%`, height: "100%", background: getScoreColor(formPct) }} />
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 700, color: getScoreColor(formPct) }}>{app.formScore}/{FORM_SCORE_MAX}</span>
                          </div>
                        </td>
                        <td style={{ padding: "11px 12px" }}>
                          {screenPct !== null
                            ? <span style={{ fontSize: 12, fontWeight: 700, color: getScoreColor(screenPct) }}>{vnT+tkT}/{COMBINED_MAX}</span>
                            : <span style={{ fontSize: 11, color: "#ccc" }}>Pending</span>}
                        </td>
                        <td style={{ padding: "11px 12px" }}>
                          <span style={{ fontSize: 10, fontWeight: 600, color: sc, background: sc+"18", padding: "3px 8px", borderRadius: 20, whiteSpace: "nowrap" }}>{app.status}</span>
                        </td>
                        <td style={{ padding: "11px 12px" }}>
                          <button onClick={() => setSelected(app)}
                            style={{ background: PURPLE, color: "#fff", border: "none", borderRadius: 7, padding: "5px 12px", fontSize: 11, cursor: "pointer", fontWeight: 600 }}>
                            Review
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function App() {
  const [view, setView] = useState("rolepicker");
  const [selectedRole, setSelectedRole] = useState(null);
  const [dashUnlocked, setDashUnlocked] = useState(false);
  const [showLock, setShowLock] = useState(false);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tapCount, setTapCount] = useState(0);

  const handleFooterTap = () => {
    const next = tapCount + 1;
    setTapCount(next);
    if (next >= 5) { setShowLock(true); setTapCount(0); }
  };

  const handleUnlock = () => {
    setDashUnlocked(true);
    setShowLock(false);
    setLoading(true);
    fbLoad().then(apps => { setApplications(apps); setLoading(false); setView("dashboard"); });
  };

  const handleSubmit = (app) => setApplications(prev => [app, ...prev.filter(a => a.id !== app.id)]);
  const handleUpdate = (updated) => setApplications(prev => prev.map(a => a.id === updated.id ? updated : a));

  if (showLock && !dashUnlocked) return <PasswordGate onSuccess={handleUnlock} />;

  if (loading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f9f9fb" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ width: 40, height: 40, border: `4px solid ${PINK}`, borderTop: "4px solid transparent", borderRadius: "50%", margin: "0 auto 16px", animation: "spin 1s linear infinite" }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{ color: PURPLE, fontWeight: 600, fontSize: 14 }}>Loading applications...</div>
      </div>
    </div>
  );

  if (view === "dashboard" && dashUnlocked) return <Dashboard applications={applications} onBack={() => setView("rolepicker")} onUpdateApp={handleUpdate} />;

  if (view === "form" && selectedRole) return (
    <div>
      <ApplicationForm roleKey={selectedRole} onSubmit={handleSubmit} onBack={() => { setSelectedRole(null); setView("rolepicker"); }} />
      <div onClick={handleFooterTap} style={{ padding: "20px", textAlign: "center", cursor: "default", userSelect: "none" }}>
        <p style={{ fontSize: 11, color: "#ccc", margin: 0 }}>Aanandam Group · Jaipur</p>
      </div>
    </div>
  );

  return (
    <div>
      <RolePicker onSelect={(k) => { setSelectedRole(k); setView("form"); }} />
      <div onClick={handleFooterTap} style={{ padding: "20px", textAlign: "center", cursor: "default", userSelect: "none" }}>
        <p style={{ fontSize: 11, color: "#ccc", margin: 0 }}>Aanandam Group · Jaipur</p>
      </div>
    </div>
  );
}
